import { BBDTag, GraviGrid, Horizontal, MaterialIcon, type MaterialSymbolIcon } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import dayjs from 'dayjs'
import React from 'react'

import type { Invoice, InvoiceGroup, InvoiceStatus } from '../../../../api/useFreight/types'
import { invoiceStatusConfig } from '../types'
import { formatters } from '@/utils/intl/dateTimeFormatters'

export const detailColumnDefs: ColDef<Invoice>[] = [
  {
    headerName: 'Invoice Number',
    field: 'invoice_number',
    cellStyle: { textTransform: 'uppercase' },
    flex: 1,
  },
  {
    headerName: 'Type',
    field: 'type',
    width: 120,
    cellRenderer: (params: ICellRendererParams) => {
      const type = params.value || 'original'
      const iconDict: Record<string, MaterialSymbolIcon> = {
        original: 'document_scanner',
        rebill: 'currency_exchange',
        credit: 'credit_card',
      }
      return (
        <BBDTag theme2={type === 'rebill'} style={{ width: 'fit-content', textTransform: 'capitalize' }}>
          <Horizontal verticalCenter horizontalCenter gap={4}>
            <MaterialIcon size={14} icon={iconDict[type]} />
            {type}
          </Horizontal>
        </BBDTag>
      )
    },
  },
  {
    headerName: 'Status',
    field: 'status',
    width: 150,
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <BBDTag
          success={params.value === 'sent'}
          warning={params.value === 'needs_attention' || params.value === 'cr_approval'}
          error={params.value === 'blocked'}
          theme2={params.value === 'ready'}
          style={{ width: 'fit-content', textTransform: 'capitalize' }}
        >
          <Horizontal verticalCenter horizontalCenter gap={8}>
            <MaterialIcon
              icon={
                params.value === 'Ready'
                  ? 'circle'
                  : invoiceStatusConfig[params.value as InvoiceStatus]?.icon || 'folder'
              }
            />
            {invoiceStatusConfig[params.value as InvoiceStatus]?.title || 'Open'}
          </Horizontal>
        </BBDTag>
      )
    },
  },
  {
    headerName: 'Sent',
    field: 'sent_date',
    flex: 1,
    width: 150,
    valueFormatter: (params) => {
      if (!params.value) return ''
      return formatters.DATE_TIME(params.value)
    },
  },
  {
    headerName: 'Exported',
    field: 'exported_at',
    minWidth: 170,
    filter: 'agDateColumnFilter',
    cellRenderer: (params: ICellRendererParams<string>) => {
      if (params.data?.status !== 'sent' || !params.value) return null
      return (
        <Horizontal style={{ overflow: 'visible' }} verticalCenter horizontalCenter gap={8}>
          <MaterialIcon style={{ color: 'var(--theme-success)' }} icon='check_circle' />
          {formatters.DATE_TIME(params.value)}
        </Horizontal>
      )
    },
  },
  {
    headerName: 'Base',
    field: 'base_amount',
    width: 120,
    type: 'numericColumn',
    valueFormatter: (params) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
  },
  {
    headerName: 'Accessorials',
    field: 'accessorial_amount',
    width: 120,
    type: 'numericColumn',
    valueFormatter: (params) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
  },
  {
    headerName: 'Total',
    field: 'total_amount',
    width: 120,
    type: 'numericColumn',
    cellStyle: { fontWeight: 'bold' },
    valueFormatter: (params) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
  },
]
export function InvoiceGroupExpand(props: {
  data: InvoiceGroup
}) {
  const { data: invoiceGroupData } = props

  // Ensure detailData is a valid array with proper validation
  const detailData = React.useMemo(() => {
    // Check if invoices is a valid array
    if (!invoiceGroupData || !Array.isArray(invoiceGroupData.invoices)) {
      return []
    }

    // Filter out any undefined or invalid invoices
    const invoices = invoiceGroupData.invoices
      .filter((invoice: Invoice) => invoice && typeof invoice === 'object')
      .map((invoice: Invoice, index: number) => ({
        ...invoice,
        // Add a unique ID if one is missing
        id: invoice.id || `invoice-${invoiceGroupData.id}-${index}`,
        // Ensure transactions is always an array for downstream components
        transactions: Array.isArray(invoice.transactions) ? invoice.transactions : [],
      }))

    return invoices
  }, [invoiceGroupData])

  // Calculate grid height based on number of rows
  const gridHeight = React.useMemo(() => {
    const rowHeight = 30 // Default row height
    const headerHeight = 40 // Default header height
    const maxVisibleRows = 30
    const numberOfRows = detailData.length

    // Make sure we have at least one row height
    if (numberOfRows === 0) {
      return `${headerHeight + rowHeight + 10}px`
    }

    // If rows are less than or equal to maxVisibleRows, set height to fit content
    if (numberOfRows <= maxVisibleRows) {
      return `${headerHeight + numberOfRows * rowHeight + 10}px`
    }

    // If more than maxVisibleRows, set height to show maxVisibleRows with scroll
    return `${headerHeight + maxVisibleRows * rowHeight + 10}px`
  }, [detailData.length])

  return (
    <div style={{ height: gridHeight, width: '100%', paddingRight: 60 }}>
      <GraviGrid
        primaryKey='id'
        rowData={detailData}
        storageKey='freight-invoice-detail-expand'
        columnDefs={detailColumnDefs}
        agPropOverrides={{
          headerHeight: 30,
          rowHeight: 30,
          getRowId: (params) => params.data?.id || params.data?.invoice_number || `invoice-${Math.random()}`,
          scrollbarWidth: 0,
          pagination: false,
          suppressPaginationPanel: true,
          rowGroupPanelShow: 'never',
          domLayout: 'normal',
          onFirstDataRendered: (params) => {
            // Force refresh to ensure all rows are visible
            setTimeout(() => {
              params.api.refreshCells({ force: true })
            }, 100)
          },
        }}
      />
    </div>
  )
}
